import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import styles from '../../styles/adminPanels.module.css';
import axiosClient from '../../api/axiosClient';

interface Ticket {
  id: string;
  account_name: string;
  account_email: string;
  subject: string;
  message: string;
  status: 'open' | 'in_progress' | 'resolved';
  admin_reply: string | null;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = { open: '#e8a86a', in_progress: '#1d60f6', resolved: '#5cb87d' };

const AdminSupport: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [replies, setReplies] = useState<Record<string, string>>({});

  const load = () => {
    setLoading(true);
    axiosClient.get('/admin/support').then(({ data }) => setTickets(data.tickets)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const sendReply = async (id: string, status: string) => {
    try {
      await axiosClient.patch(`/admin/support/${id}/reply`, { adminReply: replies[id] || undefined, status });
      setReplies((prev) => ({ ...prev, [id]: '' }));
      toast.success(status === 'resolved' ? 'Ticket marked resolved.' : 'Reply sent.');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not send reply.');
    }
  };

  return (
    <AdminShell>
      <PageHeader title="Support tickets" subtitle="Queries raised by users — reply and update status." action={<AdminWalletBadge />} />
      <div className={styles.page}>
        {loading && <div className={styles.loading}><span className={styles.spinnerLg} /></div>}
        {!loading && tickets.length === 0 && <div className={styles.empty}>No tickets yet.</div>}

        {!loading && tickets.map((t) => (
          <div key={t.id} style={{ background: 'rgba(23,22,26,.5)', border: '1px solid var(--line)', borderRadius: 14, padding: '18px 20px', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
              <b style={{ fontSize: 14 }}>{t.subject}</b>
              <span className={styles.statusPill} style={{ color: STATUS_COLORS[t.status], borderColor: STATUS_COLORS[t.status] }}>
                {t.status.replace('_', ' ')}
              </span>
            </div>
            <div className={styles.subCell}>{t.account_name} · {t.account_email} · {new Date(t.created_at).toLocaleString()}</div>
            <p style={{ fontSize: 13, marginTop: 10, color: 'var(--cream)' }}>{t.message}</p>
            {t.admin_reply && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(29,96,246,.06)', border: '1px solid rgba(29,96,246,.2)', borderRadius: 8, fontSize: 12.5 }}>
                <b style={{ color: 'var(--copper-light)' }}>Your reply:</b>
                <p style={{ color: 'var(--muted)', marginTop: 4 }}>{t.admin_reply}</p>
              </div>
            )}
            {t.status !== 'resolved' && (
              <div className={styles.replyBox}>
                <textarea
                  rows={2}
                  placeholder="Write a reply…"
                  value={replies[t.id] || ''}
                  onChange={(e) => setReplies((prev) => ({ ...prev, [t.id]: e.target.value }))}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button className={styles.actionBtn} onClick={() => sendReply(t.id, 'in_progress')}>Reply</button>
                  <button className={styles.actionBtnGhost} onClick={() => sendReply(t.id, 'resolved')}>Mark resolved</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </AdminShell>
  );
};

export default AdminSupport;
